import styled from '@emotion/styled';
import { Menu as AntdMenu } from 'antd';
import { matchRoutes, useLocation, useNavigate } from 'react-router';
import tw from 'twin.macro';

import { ROUTES } from '@/Route';
import { MENU_GROUPS } from '@/constants';
import { useMyPermissions } from '@/hooks/queries/universal';
import type { RouteKey } from '@/types/app';

const Menu = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data, availableMenuKeys } = useMyPermissions();

  return (
    <StyledMenu
      items={Object.keys(data?.permissions ?? {})
        .sort(
          (aKey, bKey) =>
            // 각 메뉴 그룹의 sort값으로 오름차순 정렬
            MENU_GROUPS[aKey as keyof typeof MENU_GROUPS].sort -
            MENU_GROUPS[bKey as keyof typeof MENU_GROUPS].sort
        )
        .map((groupKey) => ({
          key: groupKey,
          label: MENU_GROUPS[groupKey as keyof typeof MENU_GROUPS].label,
          type: 'group',
          children: Object.entries(ROUTES)
            .filter(([key, route]) => {
              return (
                route?.menuGroupKey === groupKey &&
                availableMenuKeys.some(
                  (availableMenuKey) => availableMenuKey === key
                )
              );
            })
            .map(([key, route]) => ({
              key,
              label: route.title,
              icon: route.icon,
            })),
        }))}
      selectedKeys={[
        Object.entries(ROUTES).find(([, route]) => {
          const pathPattern = matchRoutes(
            [route, ...(route?.children ?? []), ...(route?.sibling ?? [])],
            pathname
          )?.[0]?.route.path;

          return !!pathPattern;
        })?.[0] ?? '',
      ]}
      onClick={({ key }) => {
        const { path } = ROUTES[key as RouteKey];

        if (path.startsWith('http')) {
          window.open(path, '_blank');
        } else {
          navigate(path);
        }
      }}
    />
  );
};

export default Menu;

const StyledMenu = styled(AntdMenu)`
  &&& {
    ${tw`bg-transparent border-0`}

    .ant-menu-item-group {
      &:not(:first-of-type) {
        ${tw`mt-[48px]`}
      }

      &-title {
        ${tw`p-0 font-bold text-[12px] text-[rgba(0, 0 ,0, 0.45)]`}
      }

      &-list {
        ${tw`mt-[8px]`}
      }
    }

    .ant-menu-item {
      ${tw`flex flex-row items-center gap-[12px] m-0 p-[8px] text-[16px] text-[rgba(0, 0 ,0, 0.45)] [&:active]:bg-transparent [&:hover]:bg-transparent`}

      &:not(:first-of-type) {
        ${tw`mt-[4px]`}
      }

      &-selected {
        ${tw`text-[rgba(0, 0, 0, 0.88)] bg-transparent`}
      }

      &-icon {
        ${tw`w-[24px] h-[24px]`}
      }

      .ant-menu-title-content {
        ${tw`m-0 leading-[1.5]`}
      }
    }
  }
`;
